import { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import GenericFormPopup from "../../Fleet/GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import {
  orientationFieldConfiguration,
  orientationInitialValues,
} from "../../../formConfigs/Recruiting/orientationFieldConfig";
import { OrientationDto } from "../../../interfaces/Recruiting/orientation";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

const fetchAvailableTrucks = async (token: string | null | undefined) => {
  const { data } = await axios.get(
    `${apiVariable}/recruiting/orientations/available-trucks`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return data;
};

const fetchAvailableTrailers = async (token: string | null | undefined) => {
  const { data } = await axios.get(
    `${apiVariable}/recruiting/orientations/available-trailers`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return data;
};

const getUnitNumber = (fieldValue: any) => {
  if (
    fieldValue &&
    typeof fieldValue === "object" &&
    "unitNumber" in fieldValue
  ) {
    return fieldValue.unitNumber;
  }
  return undefined;
};

interface UpdateOrientationPopupProps {
  orientation: OrientationDto;
}

const UpdateOrientationPopup = ({
  orientation,
}: UpdateOrientationPopupProps) => {
  const [open, setOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<any>(
    orientationInitialValues,
  );
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: trucks = [] } = useQuery({
    queryKey: ["availableTrucks"],
    queryFn: () => fetchAvailableTrucks(token),
    enabled: open,
  });

  const { data: trailers = [] } = useQuery({
    queryKey: ["availableTrailers"],
    queryFn: () => fetchAvailableTrailers(token),
    enabled: open,
  });

  const autocompleteData = useMemo(
    () => ({
      availableTrucks: (trucks as any[]).map((truck) => ({
        id: truck.id?.toString?.() ?? truck.id,
        label: truck.unitNumber ?? truck.unit ?? truck.label ?? "Unknown",
        unitNumber: truck.unitNumber ?? truck.unit ?? "",
      })),
      availableTrailers: (trailers as any[]).map((trailer) => ({
        id: trailer.id?.toString?.() ?? trailer.id,
        label: trailer.unitNumber ?? trailer.unit ?? trailer.label ?? "Unknown",
        unitNumber: trailer.unitNumber ?? trailer.unit ?? "",
      })),
    }),
    [trucks, trailers],
  );

  useEffect(() => {
    if (!orientation) return;
    const selectedTruck =
      autocompleteData.availableTrucks.find(
        (truck) => truck.id === orientation.truckId,
      ) ||
      (orientation.truckId
        ? {
            id: orientation.truckId,
            label: orientation.truckUnitNumber ?? "Assigned Truck",
            unitNumber: orientation.truckUnitNumber ?? "",
          }
        : "");
    const selectedTrailer =
      autocompleteData.availableTrailers.find(
        (trailer) => trailer.id === orientation.trailerId,
      ) ||
      (orientation.trailerId
        ? {
            id: orientation.trailerId,
            label: orientation.trailerUnitNumber ?? "Assigned Trailer",
            unitNumber: orientation.trailerUnitNumber ?? "",
          }
        : "");

    setInitialValues({
      ...orientationInitialValues,
      ...orientation,
      truckId: selectedTruck,
      trailerId: selectedTrailer,
      orientationDate: orientation.orientationDate.split("T")[0],
    });
  }, [orientation, autocompleteData, open]);

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/recruiting/orientation`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["orientations"] });
      setOpen(false);
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to update orientation");
    },
  });

  const submitHandler = (data: any) => {
    const payload: any = {
      ...data,
      id: orientation.id,
      truckId: getId(data.truckId),
      trailerId: getId(data.trailerId),
      truckUnitNumber: getUnitNumber(data.truckId),
      trailerUnitNumber: getUnitNumber(data.trailerId),
    };

    if (payload.status === "HIRED") {
      payload.hiredDate = payload.orientationDate ?? null;
    } else {
      delete payload.hiredDate;
    }

    if (payload.status === "DECLINED") {
      payload.declinedDate = payload.orientationDate ?? null;
    } else {
      delete payload.declinedDate;
    }

    updateMutation.mutate(payload);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Update
      </Button>
      <GenericFormPopup
        formFields={orientationFieldConfiguration}
        initialValues={initialValues}
        title="Update Orientation"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        autocompleteData={autocompleteData}
        loading={updateMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Orientation updated successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default UpdateOrientationPopup;
